import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { listAttachments } from '@/features/attachments';

import { InvoicePdfPanel } from '../components/invoice-pdf-panel';
import { formatAmount } from '../lib/format-amount';
import {
  INVOICE_PDF_CATEGORY,
  invoicePdfAttachmentsKey,
  newestInvoicePdfAttachment,
} from '../lib/invoice-pdf-upload';
import type { InvoiceRow } from '../types';

// Live "has a PDF" read: the document_id FK short-circuits; otherwise the
// uploaded-attachment list is queried (only while the dialog is open) so a PDF
// attached inside this dialog flips the gate without a reload — the row
// prop's snapshot flag would not.
function useHasReceivedPdf(invoice: InvoiceRow, open: boolean): boolean {
  const listQuery = useQuery({
    queryKey: invoicePdfAttachmentsKey(invoice.id),
    enabled: open && invoice.document_id === null,
    queryFn: () =>
      listAttachments({
        entityType: 'invoice',
        entityId: invoice.id,
        category: INVOICE_PDF_CATEGORY,
      }),
  });
  if (invoice.document_id !== null) return true;
  return newestInvoicePdfAttachment(listQuery.data) !== null;
}

function ValueLine({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className="text-right text-sm font-medium tabular-nums">{value}</dd>
    </div>
  );
}

function CurrentValues({ invoice }: Readonly<{ invoice: InvoiceRow }>) {
  const currency = invoice.currency ?? '';
  return (
    <dl className="border-border flex flex-col gap-2 rounded-md border p-4">
      <ValueLine label="Invoice number" value={invoice.invoice_number ?? '—'} />
      <ValueLine label="Counterparty" value={invoice.company_name ?? '—'} />
      <ValueLine label="Quantity (MT)" value={invoice.amount_qt_mt ?? '—'} />
      <ValueLine
        label="Unit price"
        value={
          invoice.market_reference_price !== null
            ? `${formatAmount(invoice.market_reference_price, null)} ${currency}`
            : '—'
        }
      />
      <ValueLine
        label="Total"
        value={
          invoice.total_amount !== null
            ? `${formatAmount(invoice.total_amount, null)} ${currency}`
            : '—'
        }
      />
    </dl>
  );
}

export interface RecordInvoiceDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly invoice: InvoiceRow;
  readonly onContinue: () => void;
}

// The buy-side record step: recording a payable makes the counterparty's
// document official, so this re-presents the draft's current values (the seed,
// later edits, or an applied extraction) next to the received PDF — attachable
// right here — and refuses to continue without it. The server enforces the
// same gate; this is where it becomes actionable.
export function RecordInvoiceDialog(props: RecordInvoiceDialogProps) {
  const { open, onOpenChange, invoice, onContinue } = props;
  const hasPdf = useHasReceivedPdf(invoice, open);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Record invoice</DialogTitle>
          <DialogDescription>
            Confirm the values as they stand and attach the received document — a payable cannot be
            recorded without its PDF.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4 sm:grid sm:grid-cols-2">
          <CurrentValues invoice={invoice} />
          <InvoicePdfPanel invoiceId={invoice.id} />
        </div>
        {!hasPdf && (
          <p className="text-xs text-amber-600 dark:text-amber-400" role="status">
            Attach the supplier&apos;s PDF to continue.
          </p>
        )}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button type="button" disabled={!hasPdf} onClick={onContinue}>
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
